<script setup lang="ts">
/**
 * The one delete confirmation for documents (MAR-174).
 *
 * It replaces a bare `window.confirm('Delete "x"?')`, which could not answer the question a lawyer
 * actually has before deleting a legal file: *where is this used?* A document can be filed in
 * several expedientes, and the row you right-clicked shows none of them — so the dialog fetches the
 * references and names them.
 *
 * It also offers the OTHER action. Detaching and deleting are different operations with different
 * blast radii, and collapsing them into one button is what the issue explicitly warned against; when
 * the dialog is opened from inside an expediente, "just remove it from this one" is offered as the
 * lighter alternative rather than being somewhere else entirely.
 */
import { AlertTriangle, Loader2, Trash2, FolderMinus } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '~/components/ui/dialog'

// Declared once in `shared/items/itemReferences.ts`, which the handler's return type is annotated
// with. This was a local `ReferenceInfo` until 2026-08-26.
import type { ItemReferences } from '~~/shared/items/itemReferences'
import { apiErrorMessage } from '~/utils/apiErrorMessage'

const props = defineProps<{
  /** The document (or folder) to delete. `null` closes the dialog. */
  target: { id: string; name: string; type?: string } | null
  /** When the dialog is opened from inside an expediente, offer detaching from it too. */
  caseId?: string | null
}>()

const emit = defineEmits<{
  /** The user confirmed the delete and it succeeded. */
  deleted: [id: string]
  /** The user chose the lighter action instead: unlink from `caseId`. */
  detached: [id: string]
  close: []
}>()

const { t } = useI18n()
const { apiFetch } = useApi()

/** Kept in sync with `TRASH_RETENTION_DAYS` on the server. */
const RETENTION_DAYS = 30

const references = ref<ItemReferences | null>(null)
/**
 * `idle` matters as much as the others. A failed lookup used to leave `references` null, which the
 * template read as an empty case list and stated as "it isn't filed in any expediente" — a claim,
 * not a blank. Browser testing hit exactly that against a wedged dev server: the dialog confidently
 * said a document belonged to nothing while it belonged to a case. An unknown must LOOK unknown,
 * especially in a destructive confirmation.
 */
const state = ref<'idle' | 'loading' | 'ready' | 'failed'>('idle')
const busy = ref(false)
const error = ref('')

/**
 * Load the references when the dialog opens. A `watch` on `target` would be the obvious shape and is
 * exactly what the house style avoids — the open is an event, so the caller's action drives this.
 */
async function load() {
  const target = props.target
  if (!target) return
  references.value = null
  error.value = ''
  state.value = 'loading'
  try {
    references.value = await apiFetch<ItemReferences>(`/api/items/${target.id}/references`)
    state.value = 'ready'
  } catch {
    // A failed lookup must not block the delete — it only means the dialog cannot enrich itself,
    // and it must then say nothing about expedientes rather than guess.
    references.value = null
    state.value = 'failed'
  }
}
defineExpose({ load })

const isFolder = computed(() => (references.value?.type ?? props.target?.type) === 'folder')
const cases = computed(() => references.value?.cases ?? [])

async function confirmDelete() {
  const target = props.target
  if (!target || busy.value) return
  busy.value = true
  error.value = ''
  try {
    await apiFetch(`/api/items/${target.id}`, { method: 'DELETE' })
    emit('deleted', target.id)
    emit('close')
  } catch (err: any) {
    error.value = apiErrorMessage(err, t('documents.deleteDialog.failed'))
  } finally {
    busy.value = false
  }
}

async function detachInstead() {
  const target = props.target
  if (!target || !props.caseId || busy.value) return
  busy.value = true
  error.value = ''
  try {
    await apiFetch(`/api/cases/${props.caseId}/documents/${target.id}`, { method: 'DELETE' })
    emit('detached', target.id)
    emit('close')
  } catch (err: any) {
    error.value = apiErrorMessage(err, t('documents.deleteDialog.failed'))
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <!-- No type annotation on the inline handler: a template expression is compiled as plain JS, so
       `(v: boolean) =>` becomes a syntax error that only shows up on a full SSR page load — a
       client-side navigation to this page renders it fine. -->
  <Dialog :open="!!target" @update:open="v => { if (!v) emit('close') }">
    <!-- `max-w-lg`, not `md`: three buttons (detach / cancel / delete) do not fit 28rem, and
         `DialogFooter` is a non-wrapping flex row — the overflow escaped the dialog's own white
         background and painted over the modal overlay. `flex-wrap` on the footer is the belt to that
         braces, for the day a translation makes a label longer again. -->
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>
          {{ isFolder
            ? t('documents.deleteDialog.titleFolder', { name: target?.name })
            : t('documents.deleteDialog.title', { name: target?.name }) }}
        </DialogTitle>
        <DialogDescription>
          <!-- A folder without a loaded count still says it recurses (MAR-459). Falling through to
               `body` here described a folder as a single document and dropped the warning. -->
          {{ isFolder
            ? (references
              ? t('documents.deleteDialog.bodyFolder', { n: references.descendantCount, days: RETENTION_DAYS })
              : t('documents.deleteDialog.bodyFolderUnknown', { days: RETENTION_DAYS }))
            : t('documents.deleteDialog.body', { days: RETENTION_DAYS }) }}
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-3 text-sm">
        <p v-if="state === 'loading'" class="flex items-center gap-2 text-muted-foreground">
          <Loader2 class="h-3.5 w-3.5 animate-spin" /> {{ t('documents.deleteDialog.loading') }}
        </p>

        <!-- `state === 'ready'`, never a bare `else`: only a lookup that actually succeeded may make
             a claim about where this document is filed. -->
        <template v-else-if="state === 'ready' && !isFolder">
          <!-- The count is the load-bearing part: a document filed in three expedientes disappears
               from all three, and nothing on the row said so. -->
          <div v-if="cases.length" class="rounded-md border border-border bg-muted/40 p-3">
            <p class="font-medium">{{ t('documents.deleteDialog.referencedIn', cases.length) }}</p>
            <ul class="mt-1.5 space-y-0.5 text-muted-foreground">
              <li v-for="c in cases" :key="c.id" class="truncate">· {{ c.name }}</li>
            </ul>
          </div>
          <p v-else class="text-muted-foreground">{{ t('documents.deleteDialog.notReferenced') }}</p>
        </template>

        <!-- Outside the `ready` branch: losing the indexed content is true of every delete, so it is
             stated whether or not the reference lookup came back. -->
        <p v-if="state !== 'loading' && !isFolder" class="flex items-start gap-2 text-muted-foreground">
          <AlertTriangle class="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-ink" />
          {{ t('documents.deleteDialog.removesIndex') }}
        </p>

        <p v-if="error" class="text-destructive">{{ error }}</p>
      </div>

      <DialogFooter class="gap-2 sm:flex-wrap sm:justify-between">
        <!-- The lighter action, offered only where it means something. -->
        <Button
          v-if="caseId && !isFolder"
          variant="outline"
          :disabled="busy"
          :title="t('documents.deleteDialog.detachHint')"
          class="sm:mr-auto"
          @click="detachInstead"
        >
          <FolderMinus class="mr-1.5 h-4 w-4" />
          {{ t('documents.deleteDialog.detachInstead') }}
        </Button>
        <div class="flex gap-2">
          <Button variant="ghost" :disabled="busy" @click="emit('close')">{{ t('common.cancel') }}</Button>
          <Button variant="destructive" :disabled="busy" @click="confirmDelete">
            <Loader2 v-if="busy" class="mr-1.5 h-4 w-4 animate-spin" />
            <Trash2 v-else class="mr-1.5 h-4 w-4" />
            {{ t('documents.deleteDialog.confirm') }}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
